import axiosServices from '../axios';
import { DEFAULT_TIME_FORMAT, type TimeFormatPreference } from '@/common/domain/constants/timeFormat';

export interface OrganizationTimeFormatResponse {
  timeFormat: TimeFormatPreference;
}

export interface OrganizationTimeFormatError {
  message: string;
  code?: string;
  timestamp: Date;
}

const TIME_FORMAT_STORAGE_KEY = 'orgDefaultTimeFormat';

/**
 * Service for fetching organization default time format
 */
export class OrganizationTimeFormatService {
  private static instance: OrganizationTimeFormatService;
  private readonly apiUrl: string;
  private cachedTimeFormat: TimeFormatPreference | null = null;

  private constructor() {
    const { VITE_API_BASE_URL } = import.meta.env;
    if (!VITE_API_BASE_URL) {
      throw new Error('Missing environment variable: VITE_API_BASE_URL');
    }
    this.apiUrl = `${VITE_API_BASE_URL}/api/v3/global-settings/default-time-format`;
  }

  public static getInstance(): OrganizationTimeFormatService {
    if (!OrganizationTimeFormatService.instance) {
      OrganizationTimeFormatService.instance = new OrganizationTimeFormatService();
    }
    return OrganizationTimeFormatService.instance;
  }

  /**
   * Fetch the organization's default time format
   * This should be called after successful login
   */
  public async fetchOrganizationDefaultTimeFormat(): Promise<OrganizationTimeFormatResponse> {
    try {
      const response = await axiosServices.get<OrganizationTimeFormatResponse>(this.apiUrl);

      if (!response.data || !response.data.timeFormat) {
        throw new Error('Invalid response format: missing timeFormat');
      }

      return {
        timeFormat: response.data.timeFormat
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      const organizationTimeFormatError: OrganizationTimeFormatError = {
        message: `Failed to fetch organization default time format: ${errorMessage}`,
        code: error instanceof Error && 'code' in error ? String(error.code) : undefined,
        timestamp: new Date()
      };

      console.error('OrganizationTimeFormatService Error:', organizationTimeFormatError);

      throw organizationTimeFormatError;
    }
  }

  /**
   * Store organization default time format in localStorage
   */
  public storeOrganizationDefaultTimeFormat(timeFormat: TimeFormatPreference): void {
    this.cachedTimeFormat = timeFormat;
    try {
      localStorage.setItem(TIME_FORMAT_STORAGE_KEY, timeFormat);
    } catch (error) {
      console.warn('Failed to store organization default time format in localStorage:', error);
    }
  }

  /**
   * Get organization default time format from localStorage
   */
  public getOrganizationDefaultTimeFormat(): TimeFormatPreference | null {
    if (this.cachedTimeFormat) {
      return this.cachedTimeFormat;
    }
    try {
      const stored = localStorage.getItem(TIME_FORMAT_STORAGE_KEY);
      this.cachedTimeFormat = stored as TimeFormatPreference | null;
      return this.cachedTimeFormat;
    } catch (error) {
      console.warn('Failed to get organization default time format from localStorage:', error);
      return null;
    }
  }

  /**
   * Clear organization default time format from localStorage
   */
  public clearOrganizationDefaultTimeFormat(): void {
    this.cachedTimeFormat = null;
    try {
      localStorage.removeItem(TIME_FORMAT_STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear organization default time format from localStorage:', error);
    }
  }

  /**
   * Get a fallback time format when API fails
   */
  public getFallbackTimeFormat(): TimeFormatPreference {
    return DEFAULT_TIME_FORMAT;
  }

  /**
   * Fetch organization time format with fallback
   * Returns organization format if available, otherwise falls back to default
   */
  public async fetchOrganizationDefaultTimeFormatWithFallback(): Promise<OrganizationTimeFormatResponse> {
    try {
      return await this.fetchOrganizationDefaultTimeFormat();
    } catch (error) {
      console.warn('Failed to fetch organization time format, using fallback:', error);
      const fallbackTimeFormat = this.getFallbackTimeFormat();

      return {
        timeFormat: fallbackTimeFormat
      };
    }
  }
}

export const organizationTimeFormatService = OrganizationTimeFormatService.getInstance();
